import { createChartOptionFromDataSets } from 'components/BaseChart/utils/chartOptionAdapter.utils';

/** Shortens axis labels to two readable lines; tooltips still show the full forecast time. */
const formatChartTimeLabel = (label) =>
  String(label)
    .replace(/:00(?=\s*[AP]M)/i, '')
    .replace(/,? (\d{1,2}(?::\d{2})?\s*[AP]M)$/i, '\n$1')
    .replace(', ', '\n');

/**
 * Fits labels, weather icons, and zoom controls inside a forecast widget.
 * Only decorative icons are thinned out as the widget narrows. Every forecast
 * value remains in the chart and its tooltip, and the page chart is unchanged.
 */
export const buildCombinedChartOption = ({ chartData, type, containerWidth = 360 }) => {
  const option = createChartOptionFromDataSets({
    type,
    dataSets: chartData?.dataSets,
    options: chartData?.options,
  });
  if (!option) return null;

  const plotWidth = Math.max(160, containerWidth - 80);
  const maxIcons = Math.max(3, Math.floor(plotWidth / 48));
  const hasLegend = option.legend?.show !== false;

  return {
    ...option,
    title: {
      ...option.title,
      left: 12,
      top: 12,
      textStyle: {
        ...option.title?.textStyle,
        fontSize: 12,
        width: Math.max(100, containerWidth - 150),
        overflow: 'break',
      },
    },
    // Reserve space for Reset Zoom, axis labels, and any legend.
    grid: { ...option.grid, left: 12, right: 16, top: 40, bottom: hasLegend ? 32 : 12 },
    xAxis: {
      ...option.xAxis,
      axisLabel: {
        ...option.xAxis.axisLabel,
        rotate: 0,
        fontSize: 11,
        lineHeight: 14,
        margin: 10,
        // Avoid forcing a final label next to the last automatically spaced tick.
        showMaxLabel: false,
        formatter: formatChartTimeLabel,
      },
    },
    legend: { ...option.legend, type: 'scroll', bottom: 4, left: 12, right: 12 },
    tooltip: { ...option.tooltip, triggerOn: 'mousemove|click' },
    // ECharts defaults to the full range. Omitting bounds keeps the user's zoom during resizing.
    dataZoom: option.dataZoom
      .filter(({ type }) => type !== 'slider')
      .map(({ start, end, ...zoom }) => ({
        ...zoom,
        moveOnMouseMove: false,
        preventDefaultMouseMove: false,
      })),
    series: option.series.map((series) => {
      if (!series.markPoint?.data) return { ...series, symbolSize: 4 };

      const iconInterval = Math.max(1, Math.ceil(series.markPoint.data.length / maxIcons));
      const isWindDirection = series.name === 'Wind Direction';

      return {
        ...series,
        symbolSize: 4,
        markPoint: {
          ...series.markPoint,
          data: series.markPoint.data
            .filter((_, index) => index % iconInterval === 0)
            .map((point) => ({
              ...point,
              symbolSize: isWindDirection ? 24 : 26,
              symbolOffset: isWindDirection ? [0, -16] : [0, -4],
            })),
        },
      };
    }),
  };
};
